import React from "react";
import {
  compose,
  withPropsOnChange,
  withHandlers,
  withStateHandlers,
} from "recompose";
import { Trans } from "react-i18next";
import { Table, Alert } from "reactstrap";
import {
  TableContainer,
  withTable,
  withPopover,
  onMount,
  Page,
  PendingButton,
} from "components/common";
import { withXeroErrors } from "components";
import { sumBy, some } from "lodash";
import { XeroClient } from "services";
import { formatCurrency, formatDate } from "services/util";
import classNames from "classnames";
import { NotificationManager } from "react-notifications";

const statusMap = {
  DRAFT: "Draft",
  AUTHORISED: "Approved",
  SUBMITTED: "Awaiting Review",
  DECLINED: "Declined",
};

export const ApproveClaim = ({
  data,
  ordering,
  url = "expenseclaims",
  filters,
  setData,
  setXeroErrors,
  setOrdering,
  selected,
  setSelected,
  limit,
  offset,
  state,
  numSelected,
  Popover,
  togglePopover,
  pending,
  awaitingReview,
  XeroErrors,
  onDelete,
  onAuthorise,
}) => (
  <Page title="Approve Expense Claim">
    <XeroErrors />
    {awaitingReview && (
      <Alert color="warning">
        <Trans>
          You must Approve or Decline the receipts within the main Xero
          application before you can Approve this expense claim
        </Trans>
      </Alert>
    )}
    <TableContainer items={state.Receipts} idKey="ReceiptID">
      <Table size="sm">
        <thead>
          <tr>
            <th>
              <Trans>Receipt</Trans>
            </th>
            <th>
              <Trans>Status</Trans>
            </th>
            <th>
              <Trans>Ref</Trans>
            </th>
            <th>
              <Trans>Paid To</Trans>
            </th>
            <th>
              <Trans>Receipt Date</Trans>
            </th>
            <th>
              <Trans>Items</Trans>
            </th>
            <th>
              <Trans>Cost</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {state.Receipts.map((i, idx) => (
            <tr key={i.ReceiptID}>
              <td>{i.ReceiptNumber}</td>
              <td
                className={classNames({
                  "text-success": i.Status === "AUTHORISED",
                  "text-danger": i.Status === "DECLINED",
                  "text-muted": i.Status === "SUBMITTED",
                })}
              >
                <Trans>{statusMap[i.Status] || i.Status}</Trans>
              </td>
              <td>{i.Reference}</td>
              <td>{i.Contact.Name}</td>
              <td>{formatDate(i.Date)}</td>
              <td>{i.LineItems.length}</td>
              <td>{formatCurrency(i.Total)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
    <div className="d-flex flex-column align-items-end justify-content-end">
      <div />
      <table className="mb-3">
        <tbody>
          <tr>
            <td className="text-end">
              <div className="mr-5">
                <Trans>Claim Total</Trans>
              </div>
            </td>
            <td className="text-right">{formatCurrency(state.Total)}</td>
          </tr>
          <tr>
            <td className="text-end">
              <div className="mr-5">
                <Trans>Approved Total</Trans>
              </div>
            </td>
            <td className="text-right">
              {formatCurrency(
                sumBy(state.Receipts, (i) =>
                  i.Status === "AUTHORISED" ? i.Total : 0
                )
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    {!awaitingReview && (
      <div>
        <hr />
        <Trans>
          All receipts have been Approved or Declined. This expense claim can
          now be authorised.
        </Trans>
        <PendingButton
          pending={pending}
          color="primary"
          className="d-block mt-2"
          size="sm"
          onClick={onAuthorise}
        >
          Authorise
        </PendingButton>
      </div>
    )}
  </Page>
);

export const enhance = compose(
  withStateHandlers(
    {
      pending: false,
    },
    {
      setPending: () => (pending) => ({ pending }),
    }
  ),
  withPropsOnChange([], () => ({
    xero: XeroClient(),
  })),
  withPopover(),
  onMount(
    ({ match, xero }) =>
      () =>
        Promise.props({
          state: xero.get(`expenseclaims/${match.params.id}`).get("data"),
        })
  ),
  withTable(),
  withXeroErrors(),
  withPropsOnChange(["state"], ({ state: { Receipts } }) => ({
    awaitingReview: some(Receipts, (i) => i.Status === "SUBMITTED"),
  })),
  withHandlers({
    onAuthorise:
      ({ state, xero, setXeroErrors, history, setPending, t }) =>
      () => {
        setPending(true);
        xero
          .put(`expenseclaims/${state.ExpenseClaimID}`, {
            ExpenseClaimID: state.ExpenseClaimID,
            Status: "AUTHORISED",
          })
          .then(() => {
            NotificationManager.success(t("Successfully authorised"));
            history.go("/expenses/awaiting_authorisation");
          })
          .catch(setXeroErrors)
          .finally(() => setPending(false));
      },
  })
);

export default enhance(ApproveClaim);
